import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin, DatePicker, TimePicker, Checkbox, message } from 'antd';
import { ThemeButton } from '../components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { BOARDING_SERVICE_ID } from '../../constants/constants';
import { EASTERN_TIMEZONE, sortDogsByName } from '../../helpers/helpers';
import {
  useCalculatePriceMutation,
  useSendInvoiceMutation,
} from '../../services/appointments';
import { useLazyGetDogByUserIDQuery } from '../../services/dogsApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const UpdateAppointmentTimeModal = ({
  isOpen,
  onClose,
  onClick,
  isLoading,
  btnText,
  appointmentTime,
  onInvoiceSent,
  zIndex,
}) => {
  const isBoarding = appointmentTime?.service?._id === BOARDING_SERVICE_ID;
  // A paid stay is a settled invoice — the backend rejects any edit to dates,
  // times or dogs until it is unmarked as paid. Mirror that here so the modal
  // is read-only rather than failing on save.
  const isPaid = appointmentTime?.isPaid === true;
  const currentTotal =
    appointmentTime?.netTotal ??
    appointmentTime?.priceBreakdown?.total ??
    appointmentTime?.priceAtAppoTime ??
    0;

  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [dates, setDates] = useState([]); // daycare YYYY-MM-DD[]
  const [selectedDogs, setSelectedDogs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [lastSavedId, setLastSavedId] = useState(null);
  const [isTimeValid, setIsTimeValid] = useState(true);
  const debounceRef = useRef(null);

  const [calculatePrice] = useCalculatePriceMutation();
  const [sendInvoice, { isLoading: isSendingInvoice }] =
    useSendInvoiceMutation();
  const [getDogsByUserId, { data: dogsData, isLoading: loadingDogs }] =
    useLazyGetDogByUserIDQuery();

  useEffect(() => {
    if (appointmentTime) {
      const start = dayjs(appointmentTime.appointment_start_time).tz(
        EASTERN_TIMEZONE
      );
      const end = dayjs(appointmentTime.appointment_end_time).tz(
        EASTERN_TIMEZONE
      );
      setStartDate(start);
      setStartTime(start);
      setEndDate(end);
      setEndTime(end);
      setDates(appointmentTime.appointment_dates || []);
      setSelectedDogs((appointmentTime.dogs || []).map(String));
      setPreview(null);
      setLastSavedId(null);
    }
  }, [appointmentTime]);

  // Load the owner's dogs so the admin can add/remove them.
  useEffect(() => {
    if (isOpen && appointmentTime?.user?._id) {
      getDogsByUserId(appointmentTime.user._id);
    }
  }, [isOpen, appointmentTime, getDogsByUserId]);

  // The endpoint returns all of the owner's dogs, including deactivated/deleted
  // ones; only offer active dogs as selectable (matches the appointment view).
  const ownerDogs = sortDogsByName(
    (dogsData?.data?.data || dogsData?.data || []).filter(
      dog => dog?.active !== false
    )
  );

  const handleDogToggle = id => {
    const key = String(id);
    setSelectedDogs(prev => {
      if (prev.includes(key)) {
        if (prev.length === 1) {
          message.warning('An appointment must have at least one dog');
          return prev;
        }
        return prev.filter(d => d !== key);
      }
      return [...prev, key];
    });
  };

  const handleDateChange = (date, type) => {
    if (type === 'start') setStartDate(date);
    else setEndDate(date);
  };

  const handleTimeChange = (time, type) => {
    if (type === 'start') setStartTime(time);
    else setEndTime(time);
  };

  const toggleDate = day => {
    const key = day.format('YYYY-MM-DD');
    setDates(prev =>
      prev.includes(key) ? prev.filter(d => d !== key) : [...prev, key].sort()
    );
  };

  // Build an ET-offset ISO string so the wall-clock the admin picks is preserved
  // in Eastern time (matches createAppointmentModal's getEasternTimeISOForPrice).
  const formatDate = (date, time) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const hh = String(time.hour()).padStart(2, '0');
    const mm = String(time.minute()).padStart(2, '0');
    return dayjs.tz(`${dateStr} ${hh}:${mm}:00`, EASTERN_TIMEZONE).format();
  };

  // Debounced live preview against backend calculatePrice.
  useEffect(() => {
    if (!appointmentTime || !isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const numberOfDogs = selectedDogs.length || 1;
        const chargePerDay =
          appointmentTime?.chargePerDay || appointmentTime?.service?.price || 0;

        let body;
        if (isBoarding) {
          if (!startDate || !endDate || !startTime || !endTime) return;
          body = {
            service_id: appointmentTime?.service?._id,
            appointment_start_time: formatDate(startDate, startTime),
            appointment_end_time: formatDate(endDate, endTime),
            numberOfDogs,
            chargePerDay,
            discount: appointmentTime?.discount || 0,
          };
        } else {
          if (!dates.length || !startTime || !endTime) return;
          const sorted = [...dates].sort();
          body = {
            service_id: appointmentTime?.service?._id,
            appointment_start_time: formatDate(dayjs(sorted[0]), startTime),
            appointment_end_time: formatDate(
              dayjs(sorted[sorted.length - 1]),
              endTime
            ),
            appointment_dates: sorted,
            numberOfDogs,
            chargePerDay,
            discount: appointmentTime?.discount || 0,
          };
        }

        const res = await calculatePrice(body).unwrap();
        if (res?.data) setPreview(res.data);
      } catch (err) {
        // silent — preview is non-blocking
      } finally {
        setPreviewLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    isOpen,
    appointmentTime,
    startDate,
    endDate,
    startTime,
    endTime,
    dates,
    selectedDogs,
    isBoarding,
    calculatePrice,
  ]);

  // Validate check-in/check-out time ranges (mirrors createAppointmentModal).
  useEffect(() => {
    if (!startTime || !endTime) {
      setIsTimeValid(true);
      return;
    }

    const checkInHour = startTime.hour();
    const checkInMinute = startTime.minute();
    const checkOutHour = endTime.hour();
    const checkOutMinute = endTime.minute();

    if (isBoarding) {
      // Boarding: check-in 7am-12pm, check-out before 7pm.
      const checkInIsAfter7am = checkInHour >= 7;
      const checkInIsBeforeNoon =
        checkInHour < 12 || (checkInHour === 12 && checkInMinute === 0);
      const checkOutBefore7PM =
        checkOutHour < 19 || (checkOutHour === 19 && checkOutMinute === 0);

      setIsTimeValid(
        checkInIsAfter7am && checkInIsBeforeNoon && checkOutBefore7PM
      );
    } else {
      // Daycare: check-in 7am-12pm, check-out 12pm-7pm.
      const checkInBetween7andNoon =
        checkInHour >= 7 &&
        (checkInHour < 12 || (checkInHour === 12 && checkInMinute === 0));
      const checkOutBetweenNoonAnd7PM =
        (checkOutHour > 12 || (checkOutHour === 12 && checkOutMinute > 0)) &&
        (checkOutHour < 19 || (checkOutHour === 19 && checkOutMinute === 0));

      setIsTimeValid(checkInBetween7andNoon && checkOutBetweenNoonAnd7PM);
    }
  }, [startTime, endTime, isBoarding]);

  const handleSubmit = async () => {
    if (isPaid) {
      message.warning(
        'This reservation is paid. Uncheck Paid on the appointments list before editing it.'
      );
      return;
    }
    if (!isTimeValid) {
      message.warning(
        isBoarding
          ? 'Check-in must be between 7am-12pm, and check-out before 7pm'
          : 'Check-in must be between 7am-12pm, and check-out between 12pm-7pm'
      );
      return;
    }
    if (selectedDogs.length === 0) {
      message.warning('Select at least one dog');
      return;
    }
    let body;
    if (isBoarding) {
      body = {
        appointment_start_time: formatDate(startDate, startTime),
        appointment_end_time: formatDate(endDate, endTime),
        dogs: selectedDogs,
      };
    } else {
      const sorted = [...dates].sort();
      body = {
        appointment_start_time: formatDate(dayjs(sorted[0]), startTime),
        appointment_end_time: formatDate(
          dayjs(sorted[sorted.length - 1]),
          endTime
        ),
        appointment_dates: sorted,
        dogs: selectedDogs,
      };
    }
    try {
      const result = await onClick(body);
      // onClick (onUpdateTime) returns the RTK response; treat error envelope as failure.
      if (result?.data?.error || result?.error) return;
      setLastSavedId(appointmentTime?._id);
    } catch (e) {
      // parent surfaces errors
    }
  };

  const handleSendInvoice = async () => {
    try {
      await sendInvoice({ id: appointmentTime?._id }).unwrap();
      message.success('Invoice sent');
      if (onInvoiceSent) onInvoiceSent();
      onClose();
    } catch (e) {
      message.error(e?.data?.msg || 'Failed to send invoice');
    }
  };

  const delta = useMemo(() => {
    const newTotal = preview?.total ?? null;
    if (newTotal == null) return null;
    return { newTotal, diff: newTotal - currentTotal };
  }, [preview, currentTotal]);

  return (
    <Modal open={isOpen} onCancel={onClose} footer={null} zIndex={zIndex}>
      <div className="flex justify-between items-center my-6">
        <h1 className="text-xl font-semibold">{btnText}</h1>
        <ThemeButton
          className={'w-36'}
          content={
            <>{isLoading ? <Spin /> : <p className="text-sm">Update</p>}</>
          }
          onClick={handleSubmit}
          disabled={!isTimeValid || isPaid}
        />
      </div>

      {isPaid && (
        <div className="my-6 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm text-green-800">
            This reservation is <span className="font-semibold">paid</span> and
            can no longer be modified. To make a change, uncheck{' '}
            <span className="font-semibold">Paid</span> on the appointments
            list, edit the reservation, then mark it paid again.
          </p>
        </div>
      )}

      {isBoarding ? (
        <>
          <div className="my-6">
            <p className="mb-2 font-bold">Start Time</p>
            <DatePicker
              style={{ height: 40, width: 200 }}
              value={startDate}
              onChange={date => handleDateChange(date, 'start')}
              disabled={isPaid}
            />
            <TimePicker
              value={startTime}
              className="ms-5"
              style={{ height: 40, width: 200 }}
              format="h:mm A"
              onChange={time => handleTimeChange(time, 'start')}
              disabled={isPaid}
            />
          </div>
          <div className="my-6">
            <p className="mb-2 font-bold">End Time</p>
            <DatePicker
              value={endDate}
              style={{ height: 40, width: 200 }}
              onChange={date => handleDateChange(date, 'end')}
              disabled={isPaid}
            />
            <TimePicker
              value={endTime}
              className="ms-5"
              style={{ height: 40, width: 200 }}
              format="h:mm A"
              onChange={time => handleTimeChange(time, 'end')}
              disabled={isPaid}
            />
          </div>
        </>
      ) : (
        <>
          <div className="my-6">
            <p className="mb-2 font-bold">Daily Check-in Time</p>
            <TimePicker
              value={startTime}
              style={{ height: 40, width: 200 }}
              format="h:mm A"
              onChange={time => handleTimeChange(time, 'start')}
              disabled={isPaid}
            />
            <p className="mt-4 mb-2 font-bold">Daily Check-out Time</p>
            <TimePicker
              value={endTime}
              style={{ height: 40, width: 200 }}
              format="h:mm A"
              onChange={time => handleTimeChange(time, 'end')}
              disabled={isPaid}
            />
          </div>
          <div className="my-6">
            <p className="mb-2 font-bold">Daycare Dates</p>
            <div className="flex flex-wrap gap-1 mb-2">
              {dates.length === 0 && (
                <span className="text-xs text-gray-400">No dates selected</span>
              )}
              {dates.map(d => (
                <span
                  key={d}
                  className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                >
                  {d}
                  {!isPaid && (
                    <button
                      type="button"
                      className="ml-1 text-blue-600 hover:text-red-600"
                      onClick={() =>
                        setDates(prev => prev.filter(x => x !== d))
                      }
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            <DatePicker
              style={{ height: 40, width: 200 }}
              onChange={d => d && toggleDate(d)}
              placeholder="Add a date"
              disabled={isPaid}
            />
          </div>
        </>
      )}

      {!isTimeValid && (
        <div className="my-6 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-600">
            {isBoarding
              ? '⚠️ Check-in must be between 7am-12pm, and check-out before 7pm'
              : '⚠️ Check-in must be between 7am-12pm, and check-out between 12pm-7pm'}
          </p>
        </div>
      )}

      <div className="my-6">
        <p className="mb-2 font-bold">Dogs</p>
        {loadingDogs ? (
          <Spin />
        ) : ownerDogs.length === 0 ? (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              No dogs found for this client.
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {ownerDogs.map(dog => {
              const checked = selectedDogs.includes(String(dog._id));
              return (
                <div
                  key={dog._id}
                  className={`p-2 border rounded transition-colors ${
                    isPaid ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  } ${
                    checked
                      ? 'border-primary bg-blue-50'
                      : `border-gray-200 ${
                          isPaid ? '' : 'hover:border-gray-300'
                        }`
                  }`}
                  onClick={() => !isPaid && handleDogToggle(dog._id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={checked}
                      disabled={isPaid}
                      onChange={e => {
                        e.stopPropagation();
                        handleDogToggle(dog._id);
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                    <img
                      src={dog.avatar || '/default-dog-avatar.png'}
                      alt={dog.dog_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold">{dog.dog_name}</p>
                      {dog.breed && (
                        <p className="text-sm text-gray-600">{dog.breed}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">
          {selectedDogs.length} dog{selectedDogs.length === 1 ? '' : 's'}{' '}
          selected
        </p>
      </div>

      <div className="my-6 p-3 bg-gray-50 rounded">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Current total:</span>
          <span className="font-medium">${currentTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm mt-1">
          <span className="text-gray-600">New total:</span>
          <span className="font-medium">
            {previewLoading ? (
              <Spin size="small" />
            ) : delta ? (
              `$${delta.newTotal.toFixed(2)}`
            ) : (
              '—'
            )}
          </span>
        </div>
        {delta && Math.abs(delta.diff) >= 0.005 && (
          <div
            className={`flex justify-between text-sm mt-1 font-semibold ${
              delta.diff < 0 ? 'text-green-700' : 'text-amber-700'
            }`}
          >
            <span>{delta.diff < 0 ? 'Reduction:' : 'Additional:'}</span>
            <span>
              {delta.diff < 0 ? '-' : '+'}${Math.abs(delta.diff).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {lastSavedId && (
        <div className="my-4 p-3 border border-amber-200 bg-amber-50 rounded flex items-center justify-between">
          <span className="text-sm">Send updated invoice now?</span>
          <ThemeButton
            content={
              isSendingInvoice ? (
                <Spin size="small" />
              ) : (
                <p className="text-sm">Send Invoice</p>
              )
            }
            onClick={handleSendInvoice}
          />
        </div>
      )}
    </Modal>
  );
};

export default UpdateAppointmentTimeModal;
